import React, { useState } from 'react';

const GOOGLE_FORM_ACTION =
    'https://docs.google.com/forms/d/e/1FAIpQLSf7sHnMPIobUeFZE2XzKE-n47mZbvIkFnx2T_Jhd_5JrI8k-A/formResponse';

const GOOGLE_FORM_ENTRIES = {
    guestName: 'entry.63675841',
    attendance: 'entry.710173254',
    transfer: 'entry.2105544814',
    transferBack: 'entry.1254792403',
    alcohol: 'entry.2122762540',
    allergies: 'entry.851627501',
    wishes: 'entry.1919057247',
};

interface RSVPFormProps {
    guestName: string;
    multiplePersons: boolean;
    invitationId: string | undefined;
}

interface FormData {
    name: string;
    guests: string;
    attendance: string;
    transfer: string;
    transferBack: string;
    alcohol: string;
    allergies: string;
    wishes: string;
    customAlcohol: string;
    wineColor: string;
    wineTaste: string;
    champagneColor: string;
    champagneTaste: string;
}

const RSVPForm: React.FC<RSVPFormProps> = ({ guestName, invitationId, multiplePersons }) => {
    const [formData, setFormData] = useState<FormData>({
        name: guestName,
        guests: '1',
        attendance: '',
        transfer: '',
        transferBack: '',
        alcohol: '',
        allergies: '',
        wishes: '',
        customAlcohol: '',
        wineColor: '',
        wineTaste: '',
        champagneColor: '',
        champagneTaste: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'alcohol') {
            setFormData({
                ...formData,
                alcohol: value,
                customAlcohol: '',
                wineColor: value === 'Вино' ? formData.wineColor : '',
                wineTaste: value === 'Вино' ? formData.wineTaste : '',
                champagneColor: value === 'Шампанское' ? formData.champagneColor : '',
                champagneTaste: value === 'Шампанское' ? formData.champagneTaste : '',
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const buildAlcoholValue = (): string => {
        if (formData.alcohol === 'Своё') {
            return formData.customAlcohol;
        }
        if (formData.alcohol === 'Вино') {
            return [formData.alcohol, formData.wineColor, formData.wineTaste]
                .filter(Boolean)
                .join(' ');
        }
        if (formData.alcohol === 'Шампанское') {
            return [formData.alcohol, formData.champagneColor, formData.champagneTaste]
                .filter(Boolean)
                .join(' ');
        }
        return formData.alcohol;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(false);

        const alcoholValue = buildAlcoholValue();

        const body = new URLSearchParams();
        body.append(GOOGLE_FORM_ENTRIES.guestName, guestName);
        body.append(GOOGLE_FORM_ENTRIES.attendance, formData.attendance);
        body.append(GOOGLE_FORM_ENTRIES.transfer, formData.transfer);
        body.append(GOOGLE_FORM_ENTRIES.transferBack, formData.transferBack);
        body.append(GOOGLE_FORM_ENTRIES.alcohol, alcoholValue);
        body.append(GOOGLE_FORM_ENTRIES.allergies, formData.allergies);
        body.append(GOOGLE_FORM_ENTRIES.wishes, formData.wishes);

        try {
            await fetch(GOOGLE_FORM_ACTION, {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });
            setSubmitted(true);
            document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' });
        } catch {
            setError(true);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <section id="rsvp" className="bg-cream py-24 md:py-32 px-4">
                <div className="max-w-lg mx-auto text-center">
                    <span className="text-5xl mb-6 block animate-scaleIn">💌</span>
                    <h2 className="font-script text-4xl md:text-6xl text-prussian_blue mb-4">Спасибо!</h2>
                    <div className="w-12 h-px bg-gold/40 mx-auto mb-6" />
                    <p className="font-sans text-prussian_blue/60 text-base md:text-lg leading-relaxed">
                        Мы получили ваш ответ.
                    </p>
                </div>
            </section>
        );
    }

    const inputClasses =
        'w-full px-4 py-3.5 text-xs md:text-base rounded-xl bg-white font-sans text-prussian_blue input-elegant focus:outline-none transition placeholder:text-prussian_blue/30';

    return (
        <section id="rsvp" className="bg-cream py-24 md:py-32 px-4">
            <div className="max-w-lg mx-auto">
                <p className="font-sans text-[10px] md:text-xs tracking-[0.4em] uppercase text-gold mb-4">
                    Форма подтвержения
                </p>
                <h2 className="font-script text-4xl md:text-6xl text-prussian_blue mb-6">Подтверждение</h2>
                <div className="flex items-center justify-center gap-3 mb-6 divider-ornament max-w-xs mx-auto">
                    <span className="text-gold text-xl animate-heart">♥</span>
                </div>
                <p className="font-sans text-prussian_blue/50 mb-12 text-sm md:text-base">
                    Пожалуйста, заполните форму, чтобы мы могли подготовиться к вашему приходу.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6 text-left">
                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2.5 ml-1 tracking-wide">
                            Вы придёте?
                        </label>
                        <div className="flex gap-3">
                            {[
                                { value: 'Да', label: 'Да' },
                                { value: 'Нет', label: 'Нет' },
                                { value: 'Не знаю', label: 'Не знаю' },
                            ].map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`flex-1 text-center flex justify-center items-center px-3 py-3 rounded-xl cursor-pointer font-sans text-sm
                                        ${
                                            formData.attendance === opt.value
                                                ? 'bg-prussian_blue text-white'
                                                : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                        }`}>
                                    <input
                                        type="radio"
                                        name="attendance"
                                        value={opt.value}
                                        required
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2.5 ml-1 tracking-wide">
                            Необходим ли вам будет трансфер до места торжества?
                        </label>
                        <div className="flex gap-3">
                            {[
                                { value: 'Да', label: 'Да' },
                                { value: 'Нет', label: 'Нет' }
                            ].map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`flex-1 text-center flex justify-center items-center px-3 py-3 rounded-xl cursor-pointer font-sans text-sm
                                        ${
                                            formData.transfer === opt.value
                                                ? 'bg-prussian_blue text-white'
                                                : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                        }`}>
                                    <input
                                        type="radio"
                                        name="transfer"
                                        value={opt.value}
                                        required
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2.5 ml-1 tracking-wide">
                            Необходим ли вам будет трансфер обратно в город?
                        </label>
                        <div className="flex gap-3">
                            {[
                                { value: 'Да', label: 'Да' },
                                { value: 'Нет', label: 'Нет' }
                            ].map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`flex-1 text-center flex justify-center items-center px-3 py-3 rounded-xl cursor-pointer font-sans text-sm
                                        ${
                                            formData.transferBack === opt.value
                                                ? 'bg-prussian_blue text-white'
                                                : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                        }`}>
                                    <input
                                        type="radio"
                                        name="transferBack"
                                        value={opt.value}
                                        required
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2.5 ml-1 tracking-wide">
                            Предпочтения по напиткам:
                        </label>
                        <div className="flex justify-between gap-2">
                            {[
                                { value: 'Вино', label: 'Вино' },
                                { value: 'Шампанское', label: 'Шампанское' },
                                { value: 'Без алкоголя', label: 'Без алкоголя' },
                                { value: 'Своё', label: 'Свой вариант' },
                            ].map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`px-3 py-2.5 flex justify-center items-center text-center rounded-xl cursor-pointer font-sans text-xs md:text-sm
                                        ${
                                            formData.alcohol === opt.value
                                                ? 'bg-prussian_blue text-white'
                                                : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                        }`}>
                                    <input
                                        type="radio"
                                        name="alcohol"
                                        value={opt.value}
                                        required
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>

                        {formData.alcohol === 'Вино' && (
                            <div className="mt-4 space-y-4 pl-4 border-l-2 border-gold/20">
                                <div>
                                    <span className="block font-sans text-xs text-prussian_blue/50 mb-2">Цвет вина:</span>
                                    <div className="flex gap-3">
                                        {[
                                            { value: 'Красное', label: 'Красное' },
                                            { value: 'Белое', label: 'Белое' },
                                        ].map((opt) => (
                                            <label
                                                key={opt.value}
                                                className={`px-4 py-2 rounded-xl cursor-pointer font-sans text-sm
                                                    ${
                                                        formData.wineColor === opt.value
                                                            ? 'bg-prussian_blue text-white'
                                                            : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                                    }`}>
                                                <input
                                                    type="radio"
                                                    name="wineColor"
                                                    value={opt.value}
                                                    onChange={handleChange}
                                                    className="sr-only"
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <span className="block font-sans text-xs text-prussian_blue/50 mb-2">Вкус вина:</span>
                                    <div className="flex flex-wrap gap-3">
                                        {[
                                            { value: 'Сухое', label: 'Сухое' },
                                            { value: 'Полусухое', label: 'Полусухое' },
                                            { value: 'Полусладкое', label: 'Полусладкое' },
                                            { value: 'Сладкое', label: 'Сладкое' },
                                        ].map((opt) => (
                                            <label
                                                key={opt.value}
                                                className={`px-4 py-2 rounded-xl cursor-pointer font-sans text-sm
                                                    ${
                                                        formData.wineTaste === opt.value
                                                            ? 'bg-prussian_blue text-white'
                                                            : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                                    }`}>
                                                <input
                                                    type="radio"
                                                    name="wineTaste"
                                                    value={opt.value}
                                                    onChange={handleChange}
                                                    className="sr-only"
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {formData.alcohol === 'Шампанское' && (
                            <div className="mt-4 space-y-4 pl-4 border-l-2 border-gold/20">
                                <div>
                                    <span className="block font-sans text-xs text-prussian_blue/50 mb-2">Цвет шампанского:</span>
                                    <div className="flex gap-3">
                                        {[
                                            { value: 'Белое', label: 'Белое' },
                                            { value: 'Розовое', label: 'Розовое' },
                                        ].map((opt) => (
                                            <label
                                                key={opt.value}
                                                className={`px-4 py-2 rounded-xl cursor-pointer font-sans text-sm
                                                    ${
                                                        formData.champagneColor === opt.value
                                                            ? 'bg-prussian_blue text-white'
                                                            : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                                    }`}>
                                                <input
                                                    type="radio"
                                                    name="champagneColor"
                                                    value={opt.value}
                                                    onChange={handleChange}
                                                    className="sr-only"
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <span className="block font-sans text-xs text-prussian_blue/50 mb-2">Вкус шампанского:</span>
                                    <div className="flex flex-wrap gap-3">
                                        {[
                                            { value: 'Сухое', label: 'Сухое' },
                                            { value: 'Полусухое', label: 'Полусухое' },
                                            { value: 'Полусладкое', label: 'Полусладкое' },
                                            { value: 'Сладкое', label: 'Сладкое' },
                                        ].map((opt) => (
                                            <label
                                                key={opt.value}
                                                className={`px-4 py-2 rounded-xl cursor-pointer font-sans text-sm
                                                    ${
                                                        formData.champagneTaste === opt.value
                                                            ? 'bg-prussian_blue text-white'
                                                            : 'bg-white golden-border text-prussian_blue/70 hover:border-gold/50'
                                                    }`}>
                                                <input
                                                    type="radio"
                                                    name="champagneTaste"
                                                    value={opt.value}
                                                    onChange={handleChange}
                                                    className="sr-only"
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {formData.alcohol === 'Своё' && (
                            <div className="pt-4">
                                <label className="text-prussian_blue/60 font-sans text-xs md:text-sm">
                                <span className="block mb-2">
                                    {
                                        multiplePersons ? 'Напишите желаемый напиток (и укажите имя для кого напиток):' :
                                        'Напишите желаемый напиток:'
                                    }
                                    </span>
                                <textarea
                                    name="customAlcohol"
                                    value={formData.customAlcohol}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Введите ваш вариант напитка..."
                                    className={inputClasses + ' resize-none'}
                                />
                                </label>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2 ml-1 tracking-wide">
                            {
                                multiplePersons ?
                                    <>Аллергии или предпочтения в еде <span className="text-xs">(и укажите имя для кого эти предпочтения)</span>:</> :
                                    'Аллергии или предпочтения в еде:'
                            }
                        </label>
                        <textarea
                            name="allergies"
                            value={formData.allergies}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Без глютена, вегетарианец и т.д."
                            className={inputClasses + ' resize-none'}
                        />
                    </div>

                    <div>
                        <label className="block font-sans text-xs md:text-sm text-prussian_blue/60 mb-2 ml-1 tracking-wide">
                            Пожелания для молодожёнов:
                        </label>
                        <textarea
                            name="wishes"
                            value={formData.wishes}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Напишите ваши пожелания или что-нибудь приятное :)"
                            className={inputClasses + ' resize-none'}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-elegant w-full py-4 rounded-xl bg-prussian_blue text-white font-sans text-sm tracking-[0.2em] uppercase
                                   hover:bg-prussian_blue/90 active:scale-[0.98]
                                   disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 mt-2">
                        {submitting && (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        )}
                        {submitting ? 'Отправляем...' : 'Отправить'}
                    </button>

                    {error && (
                        <p className="text-red-500/80 font-sans text-xs text-center mt-3 tracking-wide">
                            Произошла ошибка. Пожалуйста, попробуйте ещё раз.
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
};

export default RSVPForm;

